import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { ANNO_CORRENTE, RUOLI, REPARTI } from '@/data/appConstants';
import { verificaAvvisi } from '@/lib/ferieEngine';
import { apiFetch } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';

const USE_DB_DATASOURCE = true;
const DEV_TOOLS_ENABLED = process.env.REACT_APP_SHOW_DEV_TOOLS !== 'false';

const AppCtx = createContext(null);

function getDatasetIdentity(isAuthenticated, me) {
  if (!isAuthenticated || !me?.id) return null;
  const stable = (value) => Array.isArray(value)
    ? value.map(String).sort()
    : [];
  return JSON.stringify([
    me.id,
    me.active_tenant_id || null,
    stable(me.tenant_ids),
    stable(me.company_ids),
    stable(me.location_ids),
    stable(me.role_codes),
    stable(me.effective_capabilities),
    Boolean(me.is_global),
  ]);
}

export function SessionAppProvider({ children }) {
  const { isAuthenticated, me } = useAuth();
  const identity = getDatasetIdentity(isAuthenticated, me);
  const key = identity || (isAuthenticated ? 'authenticated-pending-profile' : 'anonymous');
  return <AppProvider key={key}>{children}</AppProvider>;
}

export function AppProvider({ children }) {
  const { isAuthenticated, me } = useAuth();
  const datasetIdentity = getDatasetIdentity(isAuthenticated, me);
  const [currentRoleId, setCurrentRoleId] = useState(null);
  const [devImpersonateRoleId, setDevImpersonateRoleId] = useState(null);
  // DEV — simulate a specific REAL persona from the dataset. Purely client-side,
  // never touches auth.users/employees. Read-only mirror of that person's ferie,
  // documents, assignments — for QA of self-service flows.
  const [devImpersonatePersonaId, setDevImpersonatePersonaId] = useState(null);
  const [scope, setScope] = useState({ aziendaId: null, localeId: null, dipendenteId: null });

  const [aziende, setAziende]         = useState([]);
  const [locali, setLocali]           = useState([]);
  const [dipendenti, setDipendenti]   = useState([]);
  const [ferieConfig, setFerieConfig] = useState([]);
  const [storico, setStorico]         = useState([]);
  const [periodi, setPeriodi]         = useState([]);
  const [serverSaldi, setServerSaldi] = useState({});
  const [hydrated, setHydrated] = useState(false);
  const [hydrationError, setHydrationError] = useState(null);
  const [avvisi] = useState([]);

  const clearDataset = useCallback(() => {
    setAziende([]);
    setLocali([]);
    setDipendenti([]);
    setFerieConfig([]);
    setStorico([]);
    setPeriodi([]);
    setServerSaldi({});
    setHydrated(false);
  }, []);

  const applyDataset = useCallback((p) => {
    setAziende(Array.isArray(p.aziende) ? p.aziende : []);
    setLocali(Array.isArray(p.locali) ? p.locali : []);
    setDipendenti(Array.isArray(p.dipendenti) ? p.dipendenti : []);
    setFerieConfig(Array.isArray(p.ferieConfig) ? p.ferieConfig : []);
    setStorico(Array.isArray(p.storico) ? p.storico : []);
    setPeriodi(Array.isArray(p.periodi) ? p.periodi : []);
    setServerSaldi(p.serverSaldi && typeof p.serverSaldi === 'object' ? p.serverSaldi : {});
    setHydrated(true);
    setHydrationError(null);
  }, []);

  const reloadDataset = useCallback(async () => {
    try {
      const payload = await apiFetch('/api/ferie/dataset');
      applyDataset(payload);
      return payload;
    } catch (e) {
      clearDataset();
      setHydrationError(e.message || 'Dataset ferie non disponibile');
      throw e;
    }
  }, [applyDataset, clearDataset]);

  // Tenant-scoped data is available only after a successful authorized API response.
  useEffect(() => {
    clearDataset();
    setHydrationError(null);
    if (!USE_DB_DATASOURCE || !datasetIdentity) return undefined;
    let cancel = false;
    (async () => {
      try {
        const p = await apiFetch('/api/ferie/dataset');
        if (cancel) return;
        // Fetch succeeded: apply the payload ALWAYS, even when arrays are empty.
        // Empty arrays mean "no data in scope" (correct empty-state), NOT
        // "hydration failed". No static or previous-session fallback is used.
        applyDataset(p);
      } catch (e) {
        if (!cancel) {
          clearDataset();
          setHydrationError(e.message || 'Hydration failed');
        }
      }
    })();
    return () => { cancel = true; };
  }, [applyDataset, clearDataset, datasetIdentity]);

  // DEV role simulation must not select a real employee implicitly. A real
  // persona can only be selected explicitly from the hydrated dataset.
  useEffect(() => {
    if (!hydrated || !currentRoleId || currentRoleId !== 'ceo' && !devImpersonateRoleId) return;
    const roleId = currentRoleId === 'ceo' && devImpersonateRoleId
      ? devImpersonateRoleId
      : currentRoleId;
    const bySlug = (slug) => aziende.find(company => company.slug === slug);
    if (roleId === 'resp_generale') {
      const company = bySlug('ribes');
      if (company) setScope({ aziendaId: company.id, localeId: null, dipendenteId: null });
    } else if (roleId === 'resp_locale') {
      const company = bySlug('rhio');
      const location = company && locali.find(item => item.aziendaId === company.id);
      if (company && location) setScope({ aziendaId: company.id, localeId: location.id, dipendenteId: null });
    } else if (roleId === 'segretaria') {
      const company = bySlug('sushinbox');
      if (company) setScope({ aziendaId: company.id, localeId: null, dipendenteId: null });
    } else if (roleId === 'dipendente') {
      setScope({ aziendaId: null, localeId: null, dipendenteId: null });
    }
  }, [aziende, locali, dipendenti, hydrated, currentRoleId, devImpersonateRoleId]);

  // Ruolo effettivo = impersonato se CEO ha attivato dev, altrimenti il proprio
  const effectiveRoleId = currentRoleId === 'ceo' && DEV_TOOLS_ENABLED && devImpersonateRoleId
    ? devImpersonateRoleId
    : currentRoleId;
  const effectiveRole = useMemo(() => RUOLI.find(r => r.id === effectiveRoleId) || null, [effectiveRoleId]);
  const currentRole = useMemo(() => RUOLI.find(r => r.id === currentRoleId) || null, [currentRoleId]);

  const loginConRuolo = useCallback((roleId) => {
    setCurrentRoleId(roleId);
    setDevImpersonateRoleId(null);
    // Scope automatico demo:
    if (roleId === 'ceo') {
      setScope({ aziendaId: null, localeId: null, dipendenteId: null });
    } else if (roleId === 'titolare') {
      // TITOLARE = intero tenant (tutte le aziende del proprio cliente)
      setScope({ aziendaId: null, localeId: null, dipendenteId: null });
    } else if (roleId === 'resp_generale') {
      setScope({ aziendaId: 'a-ribes', localeId: null, dipendenteId: null });
    } else if (roleId === 'resp_locale') {
      setScope({ aziendaId: 'a-rhio', localeId: 'l-1', dipendenteId: null });
    } else if (roleId === 'segretaria') {
      setScope({ aziendaId: 'a-sushinbox', localeId: null, dipendenteId: null });
    } else if (roleId === 'dipendente') {
      setScope({ aziendaId: null, localeId: null, dipendenteId: null });
    }
  }, []);

  const impersona = useCallback((roleId) => {
    if (!DEV_TOOLS_ENABLED || currentRoleId !== 'ceo') return;
    setDevImpersonateRoleId(roleId);
    setDevImpersonatePersonaId(null);   // reset persona-specific override on role switch
    // Ricalcola scope come se avessi fatto login con quel ruolo
    if (!roleId) {
      setScope({ aziendaId: null, localeId: null, dipendenteId: null });
      return;
    }
    // TITOLARE = intero tenant (NON hardcodare RHIO). Il backend server-side
    // continua a restringere il vero Titolare al proprio tenant via user_role_scopes;
    // qui simuliamo correttamente il comportamento Production.
    if (roleId === 'titolare')       setScope({ aziendaId: null, localeId: null, dipendenteId: null });
    else if (roleId === 'resp_generale') setScope({ aziendaId: 'a-ribes', localeId: null, dipendenteId: null });
    else if (roleId === 'resp_locale')   setScope({ aziendaId: 'a-rhio', localeId: 'l-1', dipendenteId: null });
    else if (roleId === 'segretaria')    setScope({ aziendaId: 'a-sushinbox', localeId: null, dipendenteId: null });
    else if (roleId === 'dipendente') setScope({ aziendaId: null, localeId: null, dipendenteId: null });
  }, [currentRoleId]);

  const impersonaPersona = useCallback((legacyId) => {
    // Client-only, READ-ONLY simulation of a specific real employee. Gated on
    // the real signed-in user being global (impersona() also enforces).
    if (!DEV_TOOLS_ENABLED || currentRoleId !== 'ceo') return;
    if (!legacyId) { setDevImpersonatePersonaId(null); return; }
    // Resolve from the currently hydrated dataset — do NOT hit the DB.
    const target = dipendenti.find(d => d.id === legacyId);
    if (!target) return;
    setDevImpersonateRoleId('dipendente');
    setDevImpersonatePersonaId(legacyId);
    setScope({ aziendaId: target.aziendaId, localeId: target.localeId, dipendenteId: target.id });
  }, [currentRoleId, dipendenti]);

  const logout = useCallback(() => {
    setCurrentRoleId(null);
    setDevImpersonateRoleId(null);
    setDevImpersonatePersonaId(null);
    setScope({ aziendaId: null, localeId: null, dipendenteId: null });
  }, []);

  // Dipendenti visibili in base allo scope
  const dipendentiInScope = useMemo(() => {
    if (!effectiveRoleId) return [];
    if (effectiveRoleId === 'ceo') return dipendenti;
    if (effectiveRoleId === 'dipendente') return dipendenti.filter(d => d.id === scope.dipendenteId);
    if (scope.localeId)   return dipendenti.filter(d => d.localeId === scope.localeId);
    if (scope.aziendaId)  return dipendenti.filter(d => d.aziendaId === scope.aziendaId);
    return dipendenti;
  }, [dipendenti, scope, effectiveRoleId]);

  const canApprove = ['ceo','titolare','resp_generale','resp_locale','segretaria'].includes(effectiveRoleId);

  // === Azioni Ferie ===

  const configOf = useCallback((dipId) => ferieConfig.find(c => c.dipendenteId === dipId), [ferieConfig]);
  const storicoOf = useCallback((dipId) => (storico.find(s => s.dipendenteId === dipId)?.storico || []), [storico]);

  const serverSaldoOf = useCallback((dipId, asOfDate = new Date()) => {
    const entry = serverSaldi[dipId];
    if (!entry) return null;
    const targetDate = new Date(asOfDate);
    const dailyRows = entry.giornaliero || [];
    const targetIso = targetDate.toISOString().slice(0, 10);
    const dailyRow = dailyRows
      .filter(row => row.data <= targetIso)
      .slice(-1)[0];
    if (dailyRow) {
      const throughDaily = dailyRows.filter(row => row.data <= targetIso);
      return {
        ...entry,
        maturate: dailyRow.maturate,
        godute: throughDaily.reduce((sum, item) => sum + (item.godute || 0), 0),
        programmate: throughDaily.reduce((sum, item) => sum + (item.programmate || 0), 0),
        disponibile: dailyRow.disponibile,
        saldoData: dailyRow.data,
        mesePerMese: entry.mesePerMese || [],
      };
    }
    const month = Math.min(12, Math.max(1, asOfDate.getMonth() + 1));
    const rows = entry.mesePerMese || [];
    const rowIndex = Math.min(rows.length - 1, month - 1);
    const row = rows[rowIndex] || rows[rows.length - 1];
    if (!row) return entry;
    const through = rows.slice(0, rowIndex + 1);
    return {
      ...entry,
      maturate: row.maturate,
      godute: through.reduce((sum, item) => sum + (item.godute || 0), 0),
      programmate: through.reduce((sum, item) => sum + (item.programmate || 0), 0),
      disponibile: row.disponibile,
      mesePerMese: rows,
    };
  }, [serverSaldi]);

  const saldoOf = useCallback((dipId, asOfDate = new Date()) => (
    serverSaldoOf(dipId, asOfDate)
  ), [serverSaldoOf]);

  const previewRichiesta = useCallback((dipId, dalIso, alIso) => {
    const cfg = configOf(dipId);
    const saldo = saldoOf(dipId);
    if (!cfg || !saldo) return { giorni: 0, saldoDopo: 0, avvisi: [] };
    return verificaAvvisi({ config: cfg, saldo, dalIso, alIso, periodiEsistenti: periodi, dipendenteId: dipId });
  }, [configOf, saldoOf, periodi]);

  const inviaRichiesta = useCallback(async (dipId, dalIso, alIso, note = '') => {
    const result = await apiFetch('/api/ferie/analisi/periodi', {
      method: 'POST',
      body: JSON.stringify({
        dipendente_id: dipId,
        dal: dalIso,
        al: alIso,
        stato: 'richiesta',
        anno_competenza: Number(dalIso.slice(0, 4)),
        note: note || null,
      }),
    });
    await reloadDataset();
    return result.id;
  }, [reloadDataset]);

  const decidiRichiesta = useCallback(async (id, nuovoStato) => {
    const stato = nuovoStato === 'approvata' ? 'programmata' : nuovoStato;
    await apiFetch(`/api/ferie/analisi/periodi/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ stato }),
    });
    await reloadDataset();
  }, [reloadDataset]);

  const annullaPeriodo = useCallback((id) => decidiRichiesta(id, 'annullata'), [decidiRichiesta]);
  const marcaGoduta = useCallback((id) => decidiRichiesta(id, 'goduta'), [decidiRichiesta]);

  const aggiornaConfig = (dipId, patch) => {
    setFerieConfig(prev => prev.map(c => c.dipendenteId === dipId ? { ...c, ...patch, override: patch.ferieAnnue !== undefined ? patch.ferieAnnue !== c.ferieAnnueBase : c.override } : c));
  };

  const chiudiAnno = (dipId, anno) => {
    setStorico(prev => prev.map(s => s.dipendenteId === dipId ? {
      ...s,
      storico: s.storico.map(y => y.anno === anno ? { ...y, stato: 'chiuso', residuo: 0, note: 'Ferie fatte / Chiuso / OK' } : y),
    } : s));
  };

  const value = {
    // dati
    aziende, locali, ruoli: RUOLI, reparti: REPARTI, avvisi,
    hydrated, hydrationError,
    dipendenti, ferieConfig, storico, periodi,
    reloadDataset,
    ANNO_CORRENTE,
    // login / scope
    currentRoleId, currentRole, effectiveRoleId, effectiveRole,
    devImpersonateRoleId, devImpersonatePersonaId,
    scope,
    dipendentiInScope, canApprove,
    loginConRuolo, impersona, impersonaPersona, logout,
    // ferie engine
    configOf, storicoOf, saldoOf, previewRichiesta,
    inviaRichiesta, decidiRichiesta, annullaPeriodo, marcaGoduta,
    aggiornaConfig, chiudiAnno,
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp deve essere usato dentro AppProvider');
  return ctx;
};
